import {
	Channel,
	CommLibInstance,
	ConnectionInstance,
	ConnectionState,
	Constant,
	Contact,
	ContactLeg,
	Deferred,
	Event,
	EventBus,
	LegState,
	Log
} from "@sap/cpaas-commlib-lib";
import {Component} from "inferno";
import {Provider} from "inferno-i18next";
import {Resize} from "./components/button/Resize";
import {BlockNotification} from "./components/notification/BlockNotification";
import {Loader} from "./components/notification/Loader";
import {Notification} from "./components/notification/Notification";
import {CommlibService} from "./services/commlibService";
import {AppState, Configuration} from "./services/configuration";
import {MessageProvider, MessageType} from "./services/messageProvider";
import {GetTranslationProvider, ChangeLanguage} from "./services/translationProvider";
import {Level} from "./utils/types";
import {Chat} from "./views/chat/Chat";
import {Login} from "./views/login/Login";
import {Launchpad} from "./views/launchpad/Launchpad";
import {Socket} from "./services/ecfAdapter";
import {chatProceeding} from './utils/util';

export class App extends Component<any, any> {

	static instance: App;

	static SetLoading(loading: boolean) {
		App.instance.setState({loading});
	}

	static async ShowQuery(message: string, level?: Level, accept?: string, dismiss?: string) {
		App.instance.queryPromise = new Deferred();
		App.instance.setState({query: {message, level, accept, dismiss}, loading: false});
		return App.instance.queryPromise.promise;
	}

	static ShowMsg(message: string, level?: Level, timeout?: number) {
		App.instance.setState({msg: {message, level}});
		setTimeout(() => App.instance.setState({msg: null}), timeout || 6000);
	}

	queryPromise: Deferred;
	initPromise: Deferred;
	private translationProvider: any;
	private currenti18nCustomFileUrl: false|string;
	log = new Log("App")

	state: any = {
		loading: true,
		activeView: Views.empty,
		msg: null,
		query: null,
		width: Configuration.maxWidth,
		height: Configuration.maxHeight,
	};

	constructor(props: any) {
		super(props);
		App.instance = this;
		this.initPromise = new Deferred();
		this.initPromise.res(true);
		CommlibService.setup();
		// Set up CommLib
		Constant.setResoucePath("resources");
		CommLibInstance.getConnection().WebSocket = Socket;

		// Setup providers
		MessageProvider.setup();
		this.log.inf("Visitor start");
	}

	async componentDidMount() {
		// window.debugAnimations = true;
		EventBus.subscribe(Configuration.sizeEvent, this.onAppResize, this, Configuration.component);
		EventBus.subscribe(Configuration.startWebChatEvent, this.initWebChat, this, Configuration.component);
		EventBus.subscribe(Configuration.startLaunchpadEvent, this.initLaunchpad, this, Configuration.component);
		EventBus.subscribe(Configuration.upgradeToWebChat, this.upgradeToWebChat, this, Configuration.component);
		EventBus.subscribe(Configuration.allMessagesRecoveredEvent, this.onAllMessagesReceivedForResumedChat, this, Configuration.component);
		EventBus.subscribe(Event.Connection.connected, this.onConnected, this);
		EventBus.subscribe(Event.Connection.disconnected, this.onDisconnected, this);
		EventBus.subscribe(Event.Connection.error, this.onConnectionError, this);
		EventBus.subscribe(Event.ContactLeg(Channel.all).incoming, this.onIncomingChat, this);
		EventBus.subscribe(Event.ContactLeg(Channel.all).change, this.onChatStateChange, this);
		EventBus.subscribe(Event.Contact(Channel.all).cad, this.onCadChange, this);

		// ready to receive configuration
		MessageProvider.sendMessage(MessageType.load);
		App.SetLoading(this.state.loading);
	}

	componentWillUnmount() {
		EventBus.unsubscribe(Configuration.sizeEvent, this.onAppResize, this, Configuration.component);
		EventBus.unsubscribe(Configuration.startWebChatEvent, this.initWebChat, this, Configuration.component);
		EventBus.unsubscribe(Configuration.startLaunchpadEvent, this.initLaunchpad, this, Configuration.component);
		EventBus.unsubscribe(Configuration.upgradeToWebChat, this.upgradeToWebChat, this, Configuration.component);
		EventBus.unsubscribe(Configuration.allMessagesRecoveredEvent, this.onAllMessagesReceivedForResumedChat, this, Configuration.component);
		EventBus.unsubscribe(Event.Connection.connected, this.onConnected, this);
		EventBus.unsubscribe(Event.Connection.disconnected, this.onDisconnected, this);
		EventBus.unsubscribe(Event.Connection.error, this.onConnectionError, this);
		EventBus.unsubscribe(Event.ContactLeg(Channel.all).incoming, this.onIncomingChat, this);
	}

	componentDidUpdate(lastProps:any, lastState:any) {
		if (this.state.activeView !== lastState.activeView && this.state.activeView === Views.launchpad) {
			document.body.classList.add('transparentBgColor');
		}
		else if (this.state.activeView !== lastState.activeView && this.state.activeView !== Views.launchpad) {
			document.body.classList.remove('transparentBgColor');
		}
	}

	hasTranslationProvider () {
		return this.translationProvider ? true : false;
	}

	async changeLanguage() {
		if (Configuration.i18nCustomFileUrl && !Configuration.i18nCustomFileUrl.includes(Configuration.locale + ".properties")) {
			this.log.err("changeLanguage:Mismatching language ["+Configuration.locale+"] and custom translation file name ["+Configuration.i18nCustomFileUrl+"]");
			return;
		}
		if (!this.translationProvider || Configuration.i18nCustomFileUrl !== this.currenti18nCustomFileUrl) {
			try {
				this.log.trc("changeLanguage: Getting tranlation provider. Custom i18n: ["+Configuration.i18nCustomFileUrl+"]");
				this.currenti18nCustomFileUrl = Configuration.i18nCustomFileUrl;
				this.translationProvider = await GetTranslationProvider(Configuration.i18nCustomFileUrl);
			} catch (e) {
				App.ShowMsg("ALERT_I18N", Level.Danger);
			}
		}

		if (this.translationProvider) {
			this.log.trc("changeLanguage:Switching to ["+Configuration.locale+"]");
			try {
				await ChangeLanguage(Configuration.locale);
				this.forceUpdate();
			} catch (error) {
				this.log.err("changeLanguage:Failed to change language");
			}
		}
	}

	toView = (view: Views, forward?: boolean) => {
		this.setState({ activeView: view });
	}

	async initLaunchpad() {
		this.log.dbg("initLaunchpad", "initializing app for launchpad");
		// Launchpad view is fully client-side, and does not require websocket - simply set active view.
		this.setState({ loading: false, activeView: Views.launchpad, query: null });
	}

	async delayUntilDisconnected() {
		let waitCount = 0;
		const waitForFlagChange = (resolve:Function) => {
			if (AppState.disconnecting === false || waitCount === 5) {
				/*
				 See Chat.tsx onMessageFromOtherTab.
				 Broadcast Channel isn't 100% reliable (tabs can jam or crash, "sleeping tabs" features, mobile browsers etc).
				 It is possible that AppState.disconnecting did not get lowered on this tab.

				 Wait until flag is lowered, or wait max of 5 seconds, which should always be enough for previous chat and it's websocket to be closed.
				 Note that the 5sec maximum applies only in corner case where Broadcast Channel message did not arrive for some reason.
				*/
				AppState.disconnecting = false;
				resolve();
			} else {
				this.log.dbg("delayUntilDisconnected", "wait for previous connection close to finish");
				setTimeout(waitForFlagChange.bind(this, resolve), 1000);
				waitCount += 1;
			}
		};
		return new Promise(waitForFlagChange);
	}

	async upgradeToWebChat() {
		this.log.dbg("upgradeToWebChat", "starting web chat from launchpad");
		this.initWebChat();
	}

	async initWebChat(): Promise<boolean> {
		this.log.trc("initWebChat ", Configuration.incoming ? "resuming ongoing chat" : "starting a new web chat");
		this.setState({loading: true, query: null, activeView: Configuration.incoming ? Views.chat : Views.login});
		if (AppState.disconnecting) {
			await this.delayUntilDisconnected();
		}

		// show the UI to user
		if (!this.initPromise.resolved) {
			return this.initPromise.promise;
		}
		this.initPromise = new Deferred();
		try {
			if (ConnectionInstance.getConnectionState() !== ConnectionState.Connected) {
				await CommlibService.connect();
			}
			await CommlibService.registerDevice();
			if (Configuration.queues.length === 1 && !Configuration.showLogin && !Configuration.incoming && this.state.activeView === Views.login) {
				// user has pressed chat button to open chat
				// auto connect as only one queue configured. Wait for a short while in case ECFS want's to recover an interaction from other terminal
				setTimeout(() => {
					AppState.selectedQueue  = Configuration.queues[0].address;
					this.log.trc("initWebChat", "auto start to address: " + AppState.selectedQueue);
					this.startChat(AppState.selectedQueue);
				}, 500);
			} else if(Configuration.incoming) {
				this.log.trc("initWebChat", "incoming chat");
				// in case "incoming" flag was wrong, show the notification banner
				setTimeout(() => {
					if (!chatProceeding()) {
						Configuration.incoming = undefined;
						MessageProvider.sendMessage(MessageType.state, false);
						MessageProvider.sendMessage(MessageType.active, false);
					}
				}, 6000);
			} else {
				this.log.trc("initWebChat", "Checking if there is a chat to resume...");
				setTimeout(() => {
					if (AppState.activeLeg) {
						// Ongoing chat resumed ok
						this.log.trc("initWebChat", "chat resumed ok");
					} else if (!AppState.activeLeg && this.state.activeView === Views.chat) {
						// Could not resume previous chat - it has likely ended
						this.log.trc("initWebChat", "chat not resumed, it has likely ended - closing UI");
						Configuration.incoming = undefined;
						MessageProvider.sendMessage(MessageType.state, false);
						MessageProvider.sendMessage(MessageType.active, false);	
					}
				},1000); // Far from ideal, but allow 1sec for configuration messaging and UI states to settle in.
			}
		} catch (e) {
			this.log.wrn("initWebChat", e.message);
			this.retry();
			this.initPromise.res(false);
			return this.initPromise.promise;
		}
		this.setState({loading: Configuration.incoming });
		this.initPromise.res(true);
		return this.initPromise.promise;
	}

	async retry() {
		await App.ShowQuery("VCC_XMSG_ALERT_INTERACTION_FAIL", Level.Danger, "VCC_XBUT_ALERT_BUTTON_RETRY");
		this.log.dbg("retry");
		this.initWebChat();
	}

	startChat = async (address: string) => {
		if (!address) {
			// Some strange long time wait bug where "address" had disappeared was reported => route to login page
			this.setState({activeView: Views.login});
			return;
		}
		if (chatProceeding()) {
			this.setState({activeView: Views.chat});
			return;
		}
		this.log.dbg("startChat", address);
		this.setState({loading: true});
		try {
			await CommlibService.startChat(address);
		} catch (e) {
			this.log.wrn(e.message);
			this.retry();
			return;
		}
		this.setState({loading: false, activeView: Views.chat});
	}

	private onConnected() {
		this.setState({msg: null});
	}

	private async onDisconnected() {
		this.initPromise.res(false);
		if (AppState.disconnecting)  {
			// Websocket has disconnected intentionally, after user closed chat view & ended session
			this.log.dbg("onDisconnected", "Socket closed cleanly after chat ended");
			setTimeout(() => {
				// User might immediately start a new chat, before websocket disconnection is not fully handled yet on commlib & ecfAdapter side.
				AppState.disconnecting = false;
				this.log.dbg("onDisconnected", "Disconnection handled");
			},3000);
		}
		else {
			// Websocket disconnected unexpectedly. Commlib is (no longer) attempting to reconnect.
			this.log.wrn("onDisconnected", "Closed unexpectedly");
			await App.ShowQuery("VCC_XMSG_ALERT_CONNECTION_LOST", Level.Danger, "VCC_XBUT_ALERT_BUTTON_RETRY");
			// Init web chat again, to make one more attempt to restoring possibly ongoing chat interaction.
			// If websocket opens successfully but internaction has ended, then user has possibility to start a new interaction.
			this.initWebChat();
		}
	}

	private onConnectionError() {
		// Commlib detected unclean socket disconnect and is currently attempting to reconnect.
		App.ShowMsg("VCC_XMSG_ALERT_CONNECTION_BREAK", Level.Info, 40000);
	}

	private async onIncomingChat(channel: string, event: string, leg: ContactLeg) {
		this.log.inf("onIncomingChat", "Resuming ongoing chat [" + Configuration.incoming + "]");
		AppState.activeLeg = leg;
		this.setState({loading: Configuration.incoming });
	}

	private async onAllMessagesReceivedForResumedChat() {
		this.log.inf("onAllMessagesReceivedForResumedChat", "Chat resumed");
		this.setState({loading: false});
	}

	private async onChatStateChange(channel: string, event: string, leg: ContactLeg) {
		// Pass the state to parent site
		MessageProvider.sendMessage(MessageType.event, {event: "chatState", carry: LegState[leg.getState()]});
	}

	private async onCadChange(channel: string, event: string, contact: Contact) {
		// Pass the state to parent site
		MessageProvider.sendMessage(MessageType.event, {event: "cad", carry: contact.getCad()});
	}

	private dismissNotification = () => {
		this.setState({msg: null});
	}

	private dismissQuery = () => {
		this.setState({query: null});
		this.queryPromise.reject();
	}

	private acceptQuery = () => {
		this.setState({query: null});
		this.queryPromise.resolve();
	}

	private onAppResize(channel: string, event: string, newSize: { x: number, y: number }) {
		setTimeout(() => {
			this.setState({width: newSize.x, height: newSize.y});
		});
	}

	allowUserResize = (allow:boolean) => {
		const resizerDiv = document.getElementById('resize');
		resizerDiv.style.display = allow ? 'block' : 'none';
		this.log.dbg("allowUserResize", "Resizing allowed ["+allow+"]");
	};

	views: any = {
		[Views.login]: <Login startChat={this.startChat}/>,
		[Views.chat]: <Chat toView={this.toView}/>,
		[Views.launchpad]: <Launchpad toView={this.toView} allowUserResize={this.allowUserResize}/>,
		[Views.empty]: <div />,
	};

	render() {
		if (!this.translationProvider) {
			return <Loader width="80px" height="80px"/>;
		}
		return (
			<Provider i18next={this.translationProvider}>
				<div key={this.state.activeView} style={{width: this.state.width + "px", height: this.state.height + "px"}}>
					{this.state.msg && <Notification level={this.state.msg.level} message={this.state.msg.message} dismiss={this.dismissNotification}/>}
					{this.state.query && <BlockNotification level={this.state.query.level} message={this.state.query.message} acceptText={this.state.query.accept} accept={this.acceptQuery}
						dismissText={this.state.query.dismiss} dismiss={this.dismissQuery}/>}
					{this.state.loading && <Loader width="80px" height="80px"/>}
					{this.views[this.state.activeView]}
					<Resize/>
				</div>
			</Provider>
		);
	}
}

export enum Views {
	login, chat, launchpad, empty
}
